import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

interface Odeme {
  id: string;
  dekont_tarihi: string;
  tutar: number;
  kac_derslik: number | null;
  odeme_durumu: string | null;
  yonetici_onay: boolean | null;
  ogretmen_payi: number | null;
  aciklama: string | null;
  ogrenci?: { ogrenci_adi: string };
}

interface Student { id: string; ogrenci_adi: string; }

const statusColors: Record<string, string> = {
  'Dekont Bekleniyor': 'bg-yellow-100 text-yellow-700',
  'Dekont Geldi': 'bg-blue-100 text-blue-700',
  'Yönetici Kontrolünde': 'bg-purple-100 text-purple-700',
  Onaylandı: 'bg-green-100 text-green-700',
  'Eksik Ödeme': 'bg-red-100 text-red-700',
  İptal: 'bg-red-200 text-red-800',
};

export default function DekontGirisleriPage() {
  const { ogretmenRecord } = useAuth();
  const [data, setData] = useState<Odeme[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ studentId: '', date: '', amount: '', lessonCount: '', description: '', file: null as File | null });

  const fetchData = useCallback(async () => {
    if (!ogretmenRecord?.id) { setLoading(false); return; }
    const [{ data: rows }, { data: s }] = await Promise.all([
      supabase.from('egtpanel_odemeler')
        .select('id, dekont_tarihi, tutar, kac_derslik, odeme_durumu, yonetici_onay, ogretmen_payi, aciklama, ogrenci:egtpanel_ogrenciler(ogrenci_adi)')
        .eq('ogretmen_id', ogretmenRecord.id)
        .order('dekont_tarihi', { ascending: false }),
      supabase.from('egtpanel_ogrenciler').select('id, ogrenci_adi').eq('atanan_ogretmen_id', ogretmenRecord.id).order('ogrenci_adi'),
    ]);
    setData((rows || []) as Odeme[]);
    setStudents(s || []);
    setLoading(false);
  }, [ogretmenRecord]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ogretmenRecord?.id) return;
    setSaving(true);
    const tutar = Number(form.amount);
    const hakedisOrani = ogretmenRecord.hakedis_orani || 50;
    const ogretmenPayi = Math.round(tutar * hakedisOrani / 100);
    const platformPayi = tutar - ogretmenPayi;
    await supabase.from('egtpanel_odemeler').insert({
      ogrenci_id: form.studentId,
      ogretmen_id: ogretmenRecord.id,
      dekont_tarihi: form.date,
      tutar,
      kac_derslik: Number(form.lessonCount) || null,
      aciklama: form.description || null,
      odeme_durumu: 'Dekont Geldi',
      yonetici_onay: false,
      ogretmen_payi: ogretmenPayi,
      platform_payi: platformPayi,
      hakedis_orani: hakedisOrani,
    });
    setSaving(false);
    setShowModal(false);
    setForm({ studentId: '', date: '', amount: '', lessonCount: '', description: '', file: null });
    fetchData();
  };

  const totalSubmitted = data.reduce((a, p) => a + Number(p.tutar), 0);
  const totalApproved = data.filter(p => p.yonetici_onay).reduce((a, p) => a + Number(p.tutar), 0);
  const myShare = data.filter(p => p.yonetici_onay).reduce((a, p) => a + Number(p.ogretmen_payi || 0), 0);

  if (loading) return (
    <div className="p-6 flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dekont Girişleri</h1>
          <p className="text-slate-500 text-sm mt-1">Öğrencilerden aldığınız ödemeleri kaydedin</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-add-line" /> Dekont Gir
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Toplam Girilen', val: `₺${totalSubmitted.toLocaleString('tr-TR')}`, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Onaylanan', val: `₺${totalApproved.toLocaleString('tr-TR')}`, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Payım (Onaylı)', val: `₺${myShare.toLocaleString('tr-TR')}`, color: 'text-purple-700', bg: 'bg-purple-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <p className="text-xs text-slate-500 font-medium">{s.label}</p>
            <p className={`text-xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                {['Tarih', 'Öğrenci', 'Tutar', 'Ders Sayısı', 'Payım', 'Durum'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-16">
                  <i className="ri-receipt-line text-4xl text-slate-200 block mb-2" />
                  <p className="text-slate-400 text-sm">Dekont kaydı yok</p>
                </td></tr>
              ) : data.map(d => {
                const ogrenci = d.ogrenci as {ogrenci_adi:string} | undefined;
                return (
                  <tr key={d.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.dekont_tarihi}</td>
                    <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{ogrenci?.ogrenci_adi || '—'}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">₺{Number(d.tutar).toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.kac_derslik ? `${d.kac_derslik} ders` : '—'}</td>
                    <td className="px-4 py-3 font-medium text-purple-700 whitespace-nowrap">₺{Number(d.ogretmen_payi || 0).toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[d.odeme_durumu || ''] || 'bg-slate-100 text-slate-500'}`}>{d.odeme_durumu || '—'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Yeni Dekont Girişi</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci *</label>
                <select required value={form.studentId} onChange={e => setForm(p => ({...p, studentId: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20">
                  <option value="">— Seçiniz —</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.ogrenci_adi}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tarih *</label>
                  <input required type="date" value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tutar (₺) *</label>
                  <input required type="number" value={form.amount} onChange={e => setForm(p => ({...p, amount: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ders Sayısı</label>
                  <input type="number" value={form.lessonCount} onChange={e => setForm(p => ({...p, lessonCount: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Açıklama</label>
                <input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Dekont Görseli (Demo)</label>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-5 text-center hover:border-purple-300 transition-colors">
                  <input type="file" accept="image/*,.pdf" onChange={e => setForm(p => ({...p, file: e.target.files?.[0] || null}))} className="hidden" id="teacherFile" />
                  <label htmlFor="teacherFile" className="cursor-pointer">
                    <i className="ri-upload-cloud-line text-3xl text-slate-300 block mb-2" />
                    <p className="text-sm text-slate-400">{form.file ? form.file.name : 'Dekont görseli yükle'}</p>
                    <p className="text-xs text-slate-300 mt-1">Gerçek yükleme PHP API ile bağlanacak</p>
                  </label>
                </div>
              </div>
              {form.amount && (
                <div className="bg-purple-50 rounded-xl p-3 text-sm">
                  <div className="flex justify-between text-slate-600">
                    <span>Toplam Tutar:</span>
                    <span className="font-medium">₺{Number(form.amount).toLocaleString('tr-TR')}</span>
                  </div>
                  <div className="flex justify-between text-purple-700 font-semibold mt-1">
                    <span>Tahmini Payınız (%{ogretmenRecord?.hakedis_orani || 50}):</span>
                    <span>₺{Math.round(Number(form.amount) * ((ogretmenRecord?.hakedis_orani || 50) / 100)).toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 cursor-pointer">
                  {saving ? <i className="ri-loader-4-line animate-spin" /> : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}